import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { 
  createExpense, 
  fetchPaymentMethods,
  selectPaymentMethods,
  selectPaymentMethodsStatus,
  selectCreateExpenseStatus,
  resetCreateStatus
} from '../../../RTK/ExpensesSummary/CreateExpense';

import {
  fetchCauseTypes,
  createCauseType,
  selectAllCauseTypes,
  selectCauseTypesStatus,
  selectCreateStatus as selectCauseCreateStatus,
} from '../../../RTK/ExpensesSummary/CauseTypes';

const CreateExpenseModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const paymentMethods = useSelector(selectPaymentMethods);
  const paymentMethodsStatus = useSelector(selectPaymentMethodsStatus);
  const createStatus = useSelector(selectCreateExpenseStatus);
  const causeTypes = useSelector(selectAllCauseTypes);
  const causeTypesStatus = useSelector(selectCauseTypesStatus);
  const causeCreateStatus = useSelector(selectCauseCreateStatus);

  const [formData, setFormData] = useState({
    amount: '',
    cause: '',
    cause_type: '',
    payment_method: '',
    transaction_type: '',
    created_by: 1
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [showNewCauseType, setShowNewCauseType] = useState(false);
  const [newCauseType, setNewCauseType] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (paymentMethodsStatus === 'idle') {
        dispatch(fetchPaymentMethods());
      }
      if (causeTypesStatus === 'idle') {
        dispatch(fetchCauseTypes());
      }
    }
  }, [dispatch, isOpen, paymentMethodsStatus, causeTypesStatus]);

  useEffect(() => {
    if (createStatus === 'succeeded') {
      toast.success('تم إنشاء المصروف بنجاح');
      onSuccess();
      onClose();
      dispatch(resetCreateStatus());
    } else if (createStatus === 'failed') {
      toast.error('فشل في إنشاء المصروف');
      setShowConfirm(false);
    }
  }, [createStatus, onSuccess, onClose, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    const numericAmount = parseFloat(formData.amount);
    const numericPaymentMethod = parseInt(formData.payment_method);
    
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('الرجاء إدخال مبلغ صحيح');
      return;
    }

    dispatch(createExpense({
      ...formData,
      amount: numericAmount,
      payment_method: numericPaymentMethod
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'amount' 
        ? parseFloat(value) || '' 
        : name === 'cause_type' 
        ? parseInt(value) || '' 
        : value
    }));
  };
  

  const handleCreateCauseType = async () => {
    if (!newCauseType.trim()) {
      toast.error('الرجاء إدخال اسم نوع المصروف');
      return;
    }

    try {
      await dispatch(createCauseType({ name: newCauseType.trim() })).unwrap();
      toast.success('تم إضافة نوع المصروف بنجاح');
      setNewCauseType('');
      setShowNewCauseType(false);
    } catch (error) {
      toast.error('فشل في إضافة نوع المصروف');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-md">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-700 pb-6">
            <div className="flex items-center gap-3 text-yellow-400">
              <Plus className="w-6 h-6" />
              <h3 className="text-xl font-semibold">إنشاء مصروف جديد</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
              disabled={createStatus === 'loading'}
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {!showConfirm ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Amount Input */}
              <div className="space-y-2">
                <input
                placeholder='المبلغ'
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                  step="0.01"
                  min="0"
                />
              </div>

              {/* Cause Input */}
              <div className="space-y-2">
                <input
                placeholder='السبب'
                  type="text"
                  name="cause"
                  value={formData.cause}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                />
              </div>

              {/* Payment Method Dropdown */}
              <div className="space-y-2">
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                  disabled={paymentMethodsStatus === 'loading'}
                >
                  <option value="">اختر طريقة الدفع</option>
                  {paymentMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Transaction Type Dropdown */}
              <div className="space-y-2">
                <select
                  name="transaction_type"
                  value={formData.transaction_type}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                >
                  <option value="">اختر نوع المعاملة</option>
                  <option value="credit">إضافة</option>
                  <option value="debit">خصم</option>
                </select>
              </div>

              {/* Cause Types Dropdown */}
              <div className="space-y-2">
                <select
                  name="cause_type"
                  value={formData.cause_type}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                  required
                >
                  <option value="">اختر نوع المصروف</option>
                  {causeTypes.map(type => (
                    <option key={type.id} value={type.id} className=''>{type.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewCauseType(true)}
                  className="text-sm text-yellow-500 hover:text-yellow-400 transition-colors mt-1"
                >
                  إضافة نوع جديد
                </button>
              </div>

              {/* New Cause Type Input */}
              {showNewCauseType && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCauseType}
                      onChange={(e) => setNewCauseType(e.target.value)}
                      placeholder="اسم نوع المصروف الجديد"
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-3 text-white"
                    />
                    <button
                      type="button"
                      onClick={handleCreateCauseType}
                      disabled={causeCreateStatus === 'loading'}
                      className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50"
                    >
                      {causeCreateStatus === 'loading' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'إضافة'
                      )}
                    </button>
                  </div>
                </div>
              )}

              
              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-xl bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors"
                >
                  إنشاء المصروف
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3 text-yellow-400">
                <AlertCircle className="w-12 h-12" />
                <div>
                  <h4 className="text-lg font-medium">تأكيد إنشاء المصروف</h4>
                  <p className="text-sm text-gray-400">
                    هل أنت متأكد من إنشاء مصروف بقيمة {formData.amount}$؟
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-6 py-2 rounded-xl bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                  disabled={createStatus === 'loading'}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={createStatus === 'loading'}
                  className="px-6 py-2 rounded-xl bg-yellow-500 text-black font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {createStatus === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      جارٍ الإنشاء...
                    </>
                  ) : (
                    "تأكيد"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateExpenseModal;